// RecordPage.jsx
// The Job Record for one quote at /record/:ref, opened from the Quote Log
// side panel's Job Record button. Internal working view, light theme.
//
// Layout only: QuoteLog owns the data and every action (sends, contracts,
// PO, job dates, status changes) and passes them in, so the record page and
// the side panel always behave the same. Sections run in the order a job
// flows, enquiry to completion, on one sheet separated by rules. Actions live
// in the sticky header only.

import React, { useState, useEffect, useRef } from "react";
import { SectorPill } from "./sectors";
import { C, TONE, fmtMoney, versionOf, TestTag, StatusBadge } from "./quoteUi";

const FONT = "'IBM Plex Sans', sans-serif";
const MUTED_LABEL = { fontSize: 11, fontWeight: 500, color: C.textFaint, textTransform: "uppercase", letterSpacing: "0.06em" };

function Section({ id, title, right, children, isMobile, first }) {
  return (
    <section id={`record-${id}`} style={{ padding: isMobile ? "14px 16px 16px" : "16px 24px 18px", borderTop: first ? "none" : `1px solid ${C.border}`, scrollMarginTop: isMobile ? 130 : 96 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, minHeight: 24 }}>
        <h2 style={{ margin: 0, fontSize: 12.5, fontWeight: 600, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.09em", flex: 1 }}>{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

// Definition list: label left (40%), value right (60%). Fields flow two to
// a row on desktop so natural pairs sit side by side; wide ones take a row
// of their own, with the label column lined up with the pairs' (40% of a
// half, less its share of the gap). Empty values are left out. rowPad: the
// space above and below each row (Delivery's short fields sit tighter).
function Fields({ items, isMobile, rowPad = "5px 0" }) {
  const shown = items.filter(([, v]) => v);
  if (!shown.length) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", columnGap: 32 }}>
      {shown.map(([label, value, wide]) => {
        const span = wide && !isMobile;
        return (
          <div key={label} style={{ display: "grid", gridTemplateColumns: span ? "calc((100% - 32px) * 0.2 - 4.8px) minmax(0, 1fr)" : "2fr 3fr", gap: 12, alignItems: "baseline", padding: rowPad, minWidth: 0, gridColumn: span ? "1 / -1" : undefined }}>
            <div style={MUTED_LABEL}>{label}</div>
            <div style={{ fontSize: 13.5, color: C.text, overflowWrap: "anywhere", lineHeight: 1.4, minWidth: 0 }}>{value}</div>
          </div>
        );
      })}
    </div>
  );
}

// One line of the financials table.
function Line({ label, value, total, group }) {
  if (group) return <tr><td colSpan={2} style={{ ...MUTED_LABEL, padding: "12px 0 4px" }}>{label}</td></tr>;
  if (value == null || value === "") return null;
  const cell = { padding: "3px 0", lineHeight: 1.4, borderTop: total ? `1px solid ${C.border}` : "none", fontSize: 13.5, fontWeight: total ? 600 : 400, color: total ? C.text : "#3F4658" };
  return (
    <tr>
      <td style={cell}>{label}</td>
      <td style={{ ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", paddingLeft: 16 }}>{value}</td>
    </tr>
  );
}

const btn = { padding: "10px 16px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", minHeight: 40 };
const primaryBtn = { ...btn, border: "none", background: C.brand, color: "#fff" };
// Section-level controls (Edit, Raise PO): quiet text buttons.
const linkBtn = { border: "none", background: "none", padding: "4px 6px", margin: "-4px -6px", borderRadius: 6, color: C.brand, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", minHeight: 32 };

const badge = tone => ({ display: "inline-block", fontSize: 11.5, fontWeight: 600, padding: "1px 8px", borderRadius: 999, background: tone.bg, color: tone.text, border: `1px solid ${tone.border}`, whiteSpace: "nowrap" });
// Green when done, amber when waiting, grey when not needed / not yet.
const STATE_TONE = { done: TONE.green, waiting: TONE.amber, off: TONE.grey, problem: TONE.red, info: { bg: "#E8ECFB", text: "#2B3E9E", border: "#C9D2F5" } };

// Header "More actions" menu: [{ label, run, disabled, divider }]
function ActionsMenu({ actions, isMobile }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = e => { if (e.type === "keydown" ? e.key === "Escape" : !ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", close); };
  }, [open]);
  if (!actions.length) return null;
  return (
    <div ref={ref} style={{ position: "relative", flex: isMobile ? 1 : "0 0 auto" }}>
      <button type="button" aria-expanded={open} aria-haspopup="menu" onClick={() => setOpen(o => !o)} style={{ ...btn, minHeight: 40, width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <span>More actions</span><span aria-hidden="true" style={{ color: C.textFaint }}>{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div role="menu" style={{ position: "absolute", top: "calc(100% + 6px)", ...(isMobile
            // Phones: the button sits at the left, so the menu opens rightwards
            // from it and never runs off the left edge of the screen.
            ? { left: 0, width: "max(100%, 240px)", maxWidth: "calc(100vw - 32px)", boxSizing: "border-box" }
            : { right: 0, minWidth: 230 }), background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: "0 12px 32px rgba(20,30,70,0.16)", padding: 6, zIndex: 60 }}>
          {actions.map((a, i) => (
            <React.Fragment key={a.label}>
              {a.divider && i > 0 && <div style={{ height: 1, background: C.border, margin: "6px 4px" }} />}
              <button type="button" role="menuitem" disabled={!!a.disabled} onClick={() => { setOpen(false); a.run(); }}
                style={{ display: "block", width: "100%", textAlign: "left", border: "none", background: "none", borderRadius: 6, padding: "9px 12px", fontSize: 13.5, fontWeight: 500, color: a.disabled ? C.textFaint : C.text, cursor: a.disabled ? "default" : "pointer", fontFamily: "inherit" }}
                onMouseEnter={e => { if (!a.disabled) e.currentTarget.style.background = "#F0F3FF"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "none"; }}>
                {a.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RecordPage({
  refNo, q, loading, isMobile, onBack, onOpenRecord, successMsg,
  isSuperseded, latest, primary, nextStep, actions = [], clientHas = "",
  lossReason = "", lossNote = "", statusNote = null,
  client, onEditContact, contactEditBlock,
  job, onEditDates, jobDatesEditBlock,
  scope,
  fin,
  delivery,
  documents,
  supply,
  jobNotes = null,
  history,
}) {
  // Always-visible way back, top right (as on the other pages).
  const backBtn = (
    <button type="button" onClick={onBack} style={{ ...btn, padding: "6px 12px", minHeight: 34, fontSize: 13, background: "#F4F6FC", whiteSpace: "nowrap", flexShrink: 0 }}>← Back to Quote Log</button>
  );

  if (!q) {
    return (
      <div style={{ fontFamily: FONT, padding: isMobile ? "16px" : "24px 40px", maxWidth: 960, margin: "0 auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>{backBtn}</div>
        <div style={{ marginTop: 40, textAlign: "center", color: C.textMuted, fontSize: 15 }}>
          {loading ? `Loading ${refNo}…` : `No quote ${refNo} in the Quote Log.`}
        </div>
      </div>
    );
  }

  const qn = q["Quote Number"];
  const status = q["Status"] || "Draft";
  const short = n => String(n || "").split("-").pop();
  const hasActions = !!primary || actions.length > 0;
  const cb = fin.cb;

  return (
    <div style={{ fontFamily: FONT, color: C.text }}>
      {/* Sticky header: which quote, where it's up to, the way back, and
          every action for this job (the only place they appear). */}
      <div style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(255,255,255,0.97)", borderBottom: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(20,30,70,0.04)" }}>
        <div style={{ maxWidth: 960, margin: "0 auto", padding: isMobile ? "10px 16px" : "10px 40px 12px", boxSizing: "border-box" }}>
          <div style={{ borderLeft: `3px solid ${C.brand}`, paddingLeft: 12 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
                <h1 style={{ margin: 0, fontSize: isMobile ? 18 : 20, fontWeight: 600, letterSpacing: "-0.01em", overflowWrap: "anywhere" }}>{qn}</h1>
                <span style={{ display: "inline-block", fontSize: 11.5, fontWeight: 600, padding: "2px 9px", borderRadius: 999, background: isSuperseded ? "#F3F4F6" : "#F3E8FF", color: isSuperseded ? "#4B5563" : "#7C3AED" }}>
                  v{versionOf(qn)} · {isSuperseded ? "superseded" : "current"}
                </span>
                <TestTag q={q} />
                <StatusBadge status={status} small superseded={isSuperseded} />
                {lossReason && <span style={{ fontSize: 13, fontWeight: 600, color: TONE.red.text }}>{lossReason}</span>}
              </div>
              {backBtn}
            </div>
            {/* Lost: the note typed with the reason. Otherwise the latest note
                typed with a status change (also in History). */}
            {(lossNote || statusNote) && (
              <div style={{ fontSize: 12.5, color: C.textMuted, marginTop: 4, overflowWrap: "anywhere" }}>
                {lossNote ? <>Note: {lossNote}</> : <>{statusNote.text}{statusNote.when ? <span style={{ color: C.textFaint }}> · {statusNote.when}</span> : null}</>}
              </div>
            )}
            <div style={{ display: "flex", alignItems: isMobile ? "stretch" : "center", justifyContent: "space-between", gap: isMobile ? 10 : 16, marginTop: 6, flexDirection: isMobile ? "column" : "row" }}>
              <div style={{ fontSize: 13, color: C.textMuted, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", minWidth: 0 }}>
                {isSuperseded && latest
                  ? <span>Earlier version, read only. Current is <button type="button" onClick={() => onOpenRecord(latest["Quote Number"])} style={{ border: "none", background: "none", padding: 0, color: "#6D28D9", fontWeight: 600, cursor: "pointer", textDecoration: "underline", fontSize: 13, fontFamily: "inherit" }}>{latest["Quote Number"]}</button></span>
                  : nextStep && <span title={nextStep.title || ""} style={{ color: nextStep.tone === "green" ? TONE.green.text : TONE.amber.text, fontWeight: 500 }}>{nextStep.text}</span>}
                {q["Amendment Of"] && <span>Amends {short(q["Amendment Of"])}</span>}
                {clientHas && <span style={{ color: "#92400E", fontWeight: 600 }}>{clientHas}</span>}
              </div>
              {hasActions && (
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                  <ActionsMenu actions={actions} isMobile={isMobile} />
                  {primary && (
                    <button type="button" onClick={primary.run} disabled={!!primary.disabled}
                      style={{ ...primaryBtn, minHeight: 40, padding: "0 20px", flex: isMobile ? 1 : "0 0 auto", opacity: primary.disabled ? 0.6 : 1 }}>
                      {primary.label}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 960, margin: "0 auto", padding: isMobile ? "12px 0 40px" : "18px 40px 56px", boxSizing: "border-box" }}>
        {successMsg && <div role="status" style={{ margin: isMobile ? "0 16px 10px" : "0 0 10px", padding: "10px 14px", background: "#E6F9F0", color: "#0D5C36", border: "1px solid #27AE60", borderRadius: 8, fontSize: 13.5, fontWeight: 500 }}>✓ {successMsg}</div>}

        {/* One sheet; sections separated by rules. */}
        <div style={{ background: C.surface, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, borderLeft: isMobile ? "none" : `1px solid ${C.border}`, borderRight: isMobile ? "none" : `1px solid ${C.border}`, borderRadius: isMobile ? 0 : 12 }}>
          <Section id="client" title="Client" isMobile={isMobile} first
            right={onEditContact && !contactEditBlock && <button type="button" onClick={onEditContact} style={linkBtn}>Edit</button>}>
            {contactEditBlock || (
              <Fields isMobile={isMobile} items={[
                ["Name", client.name],
                ["Email", client.email && <a href={`mailto:${client.email}`} style={{ color: C.brand, textDecoration: "none" }}>{client.email}</a>],
                ["Company", client.company],
                ["Phone", client.phone && <a href={`tel:${client.phone.replace(/\s+/g, "")}`} style={{ color: C.brand, textDecoration: "none" }}>{client.phone}</a>],
                ["Address", client.address, true],
              ]} />
            )}
          </Section>

          <Section id="job" title="Job" isMobile={isMobile}
            right={onEditDates && !jobDatesEditBlock && <button type="button" onClick={onEditDates} style={linkBtn}>Edit dates</button>}>
            <Fields isMobile={isMobile} items={[
              ["Category", job.category && <SectorPill category={job.category} size="sm" />],
              ["Job type", job.workType],
              // Start and end on one line, the end date close after the start.
              ["Job start", !jobDatesEditBlock && <span>{job.start || <span style={badge(TONE.grey)}>Unscheduled</span>}{job.end && <span style={{ whiteSpace: "nowrap" }}><span style={{ ...MUTED_LABEL, marginLeft: 24, marginRight: 10 }}>Job end</span>{job.end}</span>}</span>, true],
              ["Location", job.location],
              ["Vehicle", job.vehicle],
              ["Application", job.application, true],
              ["Site address", job.siteAddress, true],
              ["Notes", job.notes && <span style={{ whiteSpace: "pre-line" }}>{job.notes}</span>, true],
              ["Created", job.created],
              ["Updated", job.updated],
            ]} />
            {jobDatesEditBlock && <div style={{ marginTop: 10, paddingTop: 12, borderTop: `1px dashed ${C.border}` }}>{jobDatesEditBlock}</div>}
          </Section>

          <Section id="scope" title="Scope summary" isMobile={isMobile}>
            {scope.html
              ? <div style={{ fontSize: 13.5, color: C.text, lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: scope.html }} />
              : scope.text
                ? <div style={{ fontSize: 13.5, color: C.text, lineHeight: 1.5, whiteSpace: "pre-line" }}>{scope.text}</div>
                : <div style={{ fontSize: 13.5, color: C.textFaint }}>No scope saved on this quote.</div>}
          </Section>

          <Section id="financials" title="Financials" isMobile={isMobile}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, minmax(0, 1fr))", gap: 8 }}>
              {[
                ["Total ex VAT", fin.totalExVat],
                ["Client pays inc VAT", fin.totalIncVat],
                ["Margin", fin.margin],
                ["Total direct cost", cb ? fmtMoney(cb.totalCost) : null],
              ].map(([label, value]) => (
                <div key={label} style={{ background: "#F7F8FC", border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", minWidth: 0 }}>
                  <div style={MUTED_LABEL}>{label}</div>
                  <div style={{ marginTop: 2, fontSize: 17, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value || "—"}</div>
                </div>
              ))}
            </div>
            {cb ? (
              <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6 }}>
                <tbody>
                  <Line group label="Costs" />
                  <Line label="Labour" value={fmtMoney(cb.labourCost)} />
                  {(cb.customLabour || []).map((l, i) => (
                    <Line key={i} label={`↳ ${l.label} (${Number(l.hours).toFixed(1)}h)`} value={fmtMoney(l.labourCost)} />
                  ))}
                  {cb.travelTimeCost > 0 && <Line label="Travel time (labour)" value={fmtMoney(cb.travelTimeCost)} />}
                  <Line label={`Travel (fuel/vehicle)${fin.mileage ? ` · ${fin.mileage}` : ""}`} value={fmtMoney(cb.travelCost)} />
                  <Line label="Dry ice" value={fmtMoney(cb.dryIceCost)} />
                  {cb.electricityCost > 0
                    ? <Line label="Electricity (mains)" value={fmtMoney(cb.electricityCost)} />
                    : <Line label="Fuel" value={fmtMoney(cb.fuelCost)} />}
                  <Line label="Equipment recovery" value={fmtMoney(cb.equipmentRecoveryCost)} />
                  {cb.accomCost > 0 && <Line label="Accommodation" value={fmtMoney(cb.accomCost)} />}
                  {cb.subCost > 0 && <Line label="Subsistence" value={fmtMoney(cb.subCost)} />}
                  {cb.materialsCost > 0 && <Line label="Materials" value={fmtMoney(cb.materialsCost)} />}
                  {cb.conciergeCost > 0 && <Line label="Concierge" value={fmtMoney(cb.conciergeCost)} />}
                  {cb.detailingCost > 0 && <Line label="Detailing" value={fmtMoney(cb.detailingCost)} />}
                  <Line label="Total direct cost" value={fmtMoney(cb.totalCost)} total />
                  <Line group label="Price build-up" />
                  <Line label="Base price" value={fmtMoney(cb.basePrice)} />
                  {cb.riskPct != null && <Line label={`Risk (+${cb.riskPct}%)`} value={fmtMoney(cb.afterRisk)} />}
                  <Line label={`Contingency (+${cb.contingencyPct}%)`} value={fmtMoney(cb.afterCont)} />
                  {fin.showRounded && <Line label="Rounded subtotal" value={fmtMoney(fin.preDiscountSubtotal)} />}
                  {fin.discount && (
                    <Line label={`Discount${fin.discount.reason ? ` (${fin.discount.reason})` : ""}${fin.discount.label ? `, ${fin.discount.label}` : ""}`} value={`−${fmtMoney(fin.discount.amount)}`} />
                  )}
                  <Line label="Total ex VAT" value={fmtMoney(fin.finalExVat)} total />
                </tbody>
              </table>
            ) : (
              <div style={{ marginTop: 10, fontSize: 13, color: C.textFaint }}>
                Full cost breakdown not available: this quote was saved before breakdown tracking was added.
                {fin.mileageCost && <> Mileage cost {fin.mileageCost}{fin.mileage ? ` (${fin.mileage})` : ""}.</>}
              </div>
            )}
          </Section>

          <Section id="delivery" title="Delivery" isMobile={isMobile}>
            <Fields isMobile={isMobile} items={[
              ["Quote printed", delivery.printed || "No"],
              ["Email sent", delivery.emailSent || "No"],
              ["Date sent", delivery.dateSent],
              ["Last sent to", delivery.lastSentTo, true],
            ]} />
          </Section>

          <Section id="documents" title="Documents" isMobile={isMobile}>
            {documents.staleBlock && <div style={{ marginBottom: 10 }}>{documents.staleBlock}</div>}
            <Fields isMobile={isMobile} items={documents.items.map(d => [d.label, (
              <span style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", columnGap: 8, rowGap: 2 }}>
                <span style={badge(STATE_TONE[d.tone] || STATE_TONE.off)}>{d.text}</span>
                {d.detail && <span style={{ fontSize: 12.5, color: C.textMuted }}>{d.detail}</span>}
              </span>
            )])} />
          </Section>

          <Section id="suppliers" title="Subcontractors & Suppliers" isMobile={isMobile}
            right={supply.raise && <button type="button" onClick={supply.raise.run} style={linkBtn}>+ {supply.raise.label}</button>}>
            {supply.rows.length === 0 ? (
              <div style={{ fontSize: 13.5, color: C.textMuted }}>{supply.note}</div>
            ) : isMobile ? (
              <div>
                {supply.rows.map((r, i) => (
                  <div key={r.key} style={{ padding: "8px 0", borderTop: i ? `1px solid ${C.border}` : "none" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
                      <div style={{ fontSize: 13.5, fontWeight: 500 }}>{r.supplier}</div>
                      <span style={badge(STATE_TONE[r.tone] || STATE_TONE.off)}>{r.status}</span>
                    </div>
                    <div style={{ marginTop: 2, fontSize: 13, color: "#3F4658" }}>{r.type} · {r.quantity} · {r.cost}</div>
                    {r.detail && <div style={{ marginTop: 2, fontSize: 12.5, color: C.textMuted }}>{r.detail}</div>}
                    {r.actions.length > 0 && <div style={{ display: "flex", gap: 16, marginTop: 6 }}>{r.actions.map(a => <button key={a.label} type="button" onClick={a.run} disabled={!!a.disabled} style={{ ...linkBtn, opacity: a.disabled ? 0.6 : 1 }}>{a.label}</button>)}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
                <thead>
                  <tr>{["Supplier", "Type", "Quantity", "Cost", "Status", ""].map(h => <th key={h} style={{ ...MUTED_LABEL, textAlign: "left", padding: "0 10px 4px 0", borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {supply.rows.map(r => {
                    const td = { padding: "7px 10px 7px 0", borderBottom: `1px solid ${C.border}`, verticalAlign: "baseline" };
                    return (
                      <tr key={r.key}>
                        <td style={{ ...td, fontWeight: 500 }}>{r.supplier}{r.detail && <div style={{ fontSize: 12, fontWeight: 400, color: C.textMuted, marginTop: 1 }}>{r.detail}</div>}</td>
                        <td style={td}>{r.type}</td>
                        <td style={{ ...td, fontVariantNumeric: "tabular-nums" }}>{r.quantity}</td>
                        <td style={{ ...td, fontVariantNumeric: "tabular-nums" }}>{r.cost}</td>
                        <td style={td}><span style={badge(STATE_TONE[r.tone] || STATE_TONE.off)}>{r.status}</span></td>
                        <td style={{ ...td, paddingRight: 0, textAlign: "right", whiteSpace: "nowrap" }}>
                          {r.actions.map(a => <button key={a.label} type="button" onClick={a.run} disabled={!!a.disabled} style={{ ...linkBtn, marginLeft: 10, opacity: a.disabled ? 0.6 : 1 }}>{a.label}</button>)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {supply.editBlock && <div style={{ marginTop: 10, paddingTop: 12, borderTop: `1px dashed ${C.border}` }}>{supply.editBlock}</div>}
          </Section>

          {/* Job notes: the running log of the job (JobNotes.jsx). */}
          {jobNotes}

          <Section id="history" title="History" isMobile={isMobile}>
            {history.length === 0 ? (
              <div style={{ fontSize: 13.5, color: C.textFaint }}>Nothing recorded yet.</div>
            ) : (
              <div>
                {history.map((e, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 4fr", gap: isMobile ? 0 : 12, padding: "4px 0", alignItems: "baseline" }}>
                    <div style={{ fontSize: 12, color: C.textFaint, fontVariantNumeric: "tabular-nums" }}>{e.when}</div>
                    {e.versionNumber && !e.isThis ? (
                      <button type="button" onClick={() => onOpenRecord(e.versionNumber)} title="Open this version" style={{ border: "none", background: "none", padding: 0, textAlign: "left", color: C.brand, fontWeight: 500, fontSize: 13.5, cursor: "pointer", fontFamily: "inherit" }}>{e.text} ›</button>
                    ) : (
                      <div style={{ fontSize: 13.5, fontWeight: e.versionNumber ? 500 : 400, color: C.text }}>{e.text}{e.isThis ? " (this version)" : ""}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}
